/**
 * Backup export and restore.
 *
 * A restore replaces real financial history, so it is deliberately a two-step
 * operation: validate and summarise first, then apply only after the user has
 * seen the summary and confirmed. A file that fails validation is never
 * partially applied.
 */
import {
  BACKUP_SCHEMA_VERSION,
  backupSchema,
  transactionSchema,
  type Account,
  type Backup,
  type Category,
  type MerchantRule,
  type Settings,
  type Transaction,
} from '../schemas.js';
import { totalSpentSen } from '../totals.js';
import { monthKey } from '../time.js';

export interface BuildBackupInput {
  transactions: readonly Transaction[];
  categories?: readonly Category[];
  accounts?: readonly Account[];
  merchantRules?: readonly MerchantRule[];
  settings?: Partial<Settings>;
  now?: Date;
}

export function buildBackup(input: BuildBackupInput): Backup {
  const now = input.now ?? new Date();
  return backupSchema.parse({
    schemaVersion: BACKUP_SCHEMA_VERSION,
    app: 'what-i-spent',
    exportedAt: now.toISOString(),
    settings: input.settings,
    categories: input.categories,
    accounts: input.accounts,
    merchantRules: input.merchantRules,
    transactions: input.transactions,
  });
}

export interface BackupIssue {
  path: string;
  message: string;
}

export interface BackupPreview {
  transactionCount: number;
  categoryCount: number;
  accountCount: number;
  ruleCount: number;
  /** Oldest and newest transaction dates in the file. */
  earliest?: string;
  latest?: string;
  months: { monthKey: string; count: number; totalSen: number }[];
  exportedAt: string;
  hasSettings: boolean;
}

export type BackupValidation =
  | { ok: true; backup: Backup; preview: BackupPreview; skipped: BackupIssue[] }
  | { ok: false; issues: BackupIssue[] };

/**
 * Validate a backup file without touching stored data.
 *
 * Structural problems (wrong app, wrong schema version, no transaction array)
 * fail the whole file. A single malformed transaction inside an otherwise valid
 * file is reported as skipped rather than failing the restore, so one bad row
 * cannot hold an entire history hostage.
 */
export function validateBackup(raw: unknown, timeZone?: string): BackupValidation {
  let data: unknown = raw;

  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return { ok: false, issues: [{ path: '', message: 'This file is not valid JSON.' }] };
    }
  }

  if (!data || typeof data !== 'object') {
    return { ok: false, issues: [{ path: '', message: 'This file does not contain a backup.' }] };
  }

  const record = data as Record<string, unknown>;

  if (record.app !== 'what-i-spent') {
    return {
      ok: false,
      issues: [
        { path: 'app', message: 'This backup was not created by What I Spent.' },
      ],
    };
  }
  if (record.schemaVersion !== BACKUP_SCHEMA_VERSION) {
    return {
      ok: false,
      issues: [
        {
          path: 'schemaVersion',
          message: `Unsupported backup version ${String(record.schemaVersion)}. This app reads version ${BACKUP_SCHEMA_VERSION}.`,
        },
      ],
    };
  }
  if (!Array.isArray(record.transactions)) {
    return {
      ok: false,
      issues: [{ path: 'transactions', message: 'The backup has no transactions array.' }],
    };
  }

  // Validate transactions individually so one bad record is skipped, not fatal.
  const skipped: BackupIssue[] = [];
  const transactions: Transaction[] = [];
  record.transactions.forEach((entry, index) => {
    const result = transactionSchema.safeParse(entry);
    if (result.success) {
      transactions.push(result.data);
    } else {
      skipped.push({
        path: `transactions[${index}]`,
        message: result.error.issues
          .map((issue) => `${issue.path.join('.') || 'value'}: ${issue.message}`)
          .join('; '),
      });
    }
  });

  const parsed = backupSchema.safeParse({ ...record, transactions });
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      })),
    };
  }

  return { ok: true, backup: parsed.data, preview: previewBackup(parsed.data, timeZone), skipped };
}

/** A summary of what a restore would bring in, shown before anything is replaced. */
export function previewBackup(backup: Backup, timeZone?: string): BackupPreview {
  const sorted = [...backup.transactions].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  const months = new Map<string, { count: number; transactions: Transaction[] }>();

  for (const transaction of backup.transactions) {
    const key = monthKey(transaction.occurredAt, timeZone);
    const entry = months.get(key) ?? { count: 0, transactions: [] };
    entry.count += 1;
    entry.transactions.push(transaction);
    months.set(key, entry);
  }

  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  return {
    transactionCount: backup.transactions.length,
    categoryCount: backup.categories?.length ?? 0,
    accountCount: backup.accounts?.length ?? 0,
    ruleCount: backup.merchantRules?.length ?? 0,
    ...(first ? { earliest: first.occurredAt } : {}),
    ...(last ? { latest: last.occurredAt } : {}),
    months: [...months.entries()]
      .map(([key, entry]) => ({
        monthKey: key,
        count: entry.count,
        totalSen: totalSpentSen(entry.transactions, backup.categories ?? []),
      }))
      .sort((a, b) => b.monthKey.localeCompare(a.monthKey)),
    exportedAt: backup.exportedAt,
    hasSettings: Boolean(backup.settings),
  };
}

export type RestoreMode = 'replace' | 'merge';

export interface RestorePlan {
  mode: RestoreMode;
  /** Records that exist only in the backup. */
  toInsert: Transaction[];
  /** Records that exist in both and are newer in the backup. */
  toUpdate: Transaction[];
  /** Records in both where the local copy is newer and is kept. */
  keptLocal: Transaction[];
  /** Records that only exist locally and would be removed by a replace. */
  toRemove: Transaction[];
}

/**
 * Work out exactly what a restore would do, before it does it.
 * `merge` never removes anything; `replace` lists what it would drop so the
 * confirmation dialog can say so in plain numbers.
 */
export function planRestore(
  backup: Backup,
  existing: readonly Transaction[],
  mode: RestoreMode = 'merge',
): RestorePlan {
  const localById = new Map(existing.map((transaction) => [transaction.id, transaction]));
  const backupIds = new Set(backup.transactions.map((transaction) => transaction.id));

  const toInsert: Transaction[] = [];
  const toUpdate: Transaction[] = [];
  const keptLocal: Transaction[] = [];

  for (const incoming of backup.transactions) {
    const local = localById.get(incoming.id);
    if (!local) {
      toInsert.push(incoming);
    } else if (incoming.updatedAt > local.updatedAt) {
      toUpdate.push(incoming);
    } else {
      keptLocal.push(local);
    }
  }

  const toRemove =
    mode === 'replace' ? existing.filter((transaction) => !backupIds.has(transaction.id)) : [];

  return { mode, toInsert, toUpdate, keptLocal, toRemove };
}
